// src/utils/contactsUtils.js
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy, 
  Timestamp 
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase/config';

export const contactsService = {
  /**
   * Create a new contact
   */
  createContact: async (organizationId, contactData) => {
    try {
      const contactsRef = collection(db, 'parrot-organizations', organizationId, 'contacts');
      const newContact = {
        ...contactData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      
      const docRef = await addDoc(contactsRef, newContact);
      return { id: docRef.id, ...newContact };
    } catch (error) {
      console.error('Error creating contact:', error);
      throw new Error(`Failed to create contact: ${error.message}`);
    }
  },

  /**
   * Update an existing contact
   */
  updateContact: async (organizationId, contactId, contactData) => {
    try {
      const contactRef = doc(db, 'parrot-organizations', organizationId, 'contacts', contactId);
      const updatedData = {
        ...contactData,
        updatedAt: Timestamp.now()
      };
      
      await updateDoc(contactRef, updatedData);
      return { id: contactId, ...updatedData };
    } catch (error) {
      console.error('Error updating contact:', error);
      throw new Error(`Failed to update contact: ${error.message}`);
    }
  },

  /**
   * Delete a contact
   */
  deleteContact: async (organizationId, contactId) => {
    try {
      const contactRef = doc(db, 'parrot-organizations', organizationId, 'contacts', contactId);
      await deleteDoc(contactRef);
      return true;
    } catch (error) {
      console.error('Error deleting contact:', error);
      throw new Error(`Failed to delete contact: ${error.message}`);
    }
  },

  /**
   * Get all contacts for an organization
   */
  getContacts: async (organizationId) => {
    try {
      const contactsRef = collection(db, 'parrot-organizations', organizationId, 'contacts');
      const q = query(contactsRef, orderBy('name', 'asc'));
      const snapshot = await getDocs(q);
      
      const contacts = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        contacts.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate()?.toISOString(),
          updatedAt: data.updatedAt?.toDate()?.toISOString()
        });
      });
      
      return contacts;
    } catch (error) {
      console.error('Error fetching contacts:', error);
      throw new Error(`Failed to fetch contacts: ${error.message}`);
    }
  },

  /**
   * Get a single contact by ID
   */
  getContact: async (organizationId, contactId) => {
    try {
      const contactRef = doc(db, 'parrot-organizations', organizationId, 'contacts', contactId);
      const contactSnap = await getDoc(contactRef);
      
      if (contactSnap.exists()) {
        const data = contactSnap.data();
        return {
          id: contactSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate()?.toISOString(),
          updatedAt: data.updatedAt?.toDate()?.toISOString()
        };
      } else {
        throw new Error('Contact not found');
      }
    } catch (error) {
      console.error('Error getting contact:', error);
      throw error;
    }
  },

  /**
   * Send LinkedIn messages to multiple contacts
   */
  sendLinkedInMessages: async (organizationId, messageData) => {
    try {
      const messagesRef = collection(db, 'parrot-organizations', organizationId, 'linkedin-messages');
      
      // Create a message record for tracking
      const messageRecord = {
        subject: messageData.subject,
        message: messageData.message,
        contactIds: messageData.contacts.map(c => c.id),
        contactNames: messageData.contacts.map(c => c.name),
        linkedinProfiles: messageData.contacts.map(c => c.linkedin),
        sentBy: messageData.sentBy,
        senderName: messageData.senderName,
        sentAt: Timestamp.now(),
        status: 'logged', // Since we're not actually sending through LinkedIn API
        type: 'linkedin'
      };
      
      const docRef = await addDoc(messagesRef, messageRecord);
      
      // Also log individual message entries for each contact
      const individualMessages = messageData.contacts.map(contact => ({
        contactId: contact.id,
        contactName: contact.name,
        linkedinProfile: contact.linkedin,
        subject: messageData.subject,
        message: messageData.message,
        sentBy: messageData.sentBy,
        senderName: messageData.senderName,
        sentAt: Timestamp.now(),
        messageGroupId: docRef.id,
        type: 'linkedin',
        status: 'logged'
      }));
      
      // Add individual message records
      const contactMessagesRef = collection(db, 'parrot-organizations', organizationId, 'contact-messages');
      await Promise.all(
        individualMessages.map(msg => addDoc(contactMessagesRef, msg))
      );
      
      return { id: docRef.id, ...messageRecord };
    } catch (error) {
      console.error('Error sending LinkedIn messages:', error);
      throw new Error(`Failed to send LinkedIn messages: ${error.message}`);
    }
  },

  /**
   * Get contact communication history
   */
  getContactHistory: async (organizationId, contactIds = []) => {
    try {
      const history = [];
      
      // Get LinkedIn messages
      const linkedinRef = collection(db, 'parrot-organizations', organizationId, 'contact-messages');
      let linkedinQuery = query(linkedinRef, orderBy('sentAt', 'desc'));
      
      if (contactIds.length > 0) {
        linkedinQuery = query(linkedinRef, 
          where('contactId', 'in', contactIds), 
          orderBy('sentAt', 'desc')
        );
      }
      
      const linkedinSnapshot = await getDocs(linkedinQuery);
      linkedinSnapshot.forEach(doc => {
        const data = doc.data();
        history.push({
          id: doc.id,
          type: 'linkedin',
          contactIds: [data.contactId],
          subject: data.subject,
          content: data.message,
          preview: data.message?.substring(0, 100) + '...',
          sentAt: data.sentAt?.toDate()?.toISOString(),
          senderName: data.senderName,
          status: data.status
        });
      });
      
      // Get email messages (from the existing email system)
      const emailRef = collection(db, 'parrot-mail', organizationId, 'emails');
      const emailQuery = query(emailRef, orderBy('sentAt', 'desc'));
      const emailSnapshot = await getDocs(emailQuery);
      
      emailSnapshot.forEach(doc => {
        const data = doc.data();
        // Try to match emails to contacts by email address
        const matchingContactIds = contactIds.filter(contactId => {
          // This would need to be enhanced with actual contact email matching
          return data.to?.some(recipient => 
            // You'd need to implement contact email matching logic here
            false
          );
        });
        
        if (contactIds.length === 0 || matchingContactIds.length > 0) {
          history.push({
            id: doc.id,
            type: 'email',
            contactIds: matchingContactIds,
            subject: data.subject,
            content: data.htmlBody || data.textBody,
            preview: (data.textBody || data.htmlBody)?.substring(0, 100) + '...',
            sentAt: data.sentAt?.toDate()?.toISOString(),
            senderName: data.senderName,
            status: data.status
          });
        }
      });
      
      // Sort all history by date
      history.sort((a, b) => new Date(b.sentAt) - new Date(a.sentAt));
      
      return history;
    } catch (error) {
      console.error('Error fetching contact history:', error);
      throw new Error(`Failed to fetch contact history: ${error.message}`);
    }
  },

  /**
   * Generate LinkedIn message using AI
   */
  generateLinkedInMessage: async (prompt, context = {}) => {
    try {
      const functions = getFunctions();
      const generateContentFn = httpsCallable(functions, 'generateAIContent');
      
      const aiPrompt = `Generate a professional LinkedIn message. Context:
      - Recipients: ${context.recipientNames?.join(', ') || 'Professional contacts'}
      - Sender: ${context.senderName || 'Professional'}
      - Organization: ${context.organizationName || 'Business'}
      
      User request: ${prompt}
      
      Please provide both a subject line and message content. Keep the message professional, concise (under 300 characters), and personalized.`;
      
      const result = await generateContentFn({
        prompt: aiPrompt,
        context: {
          useCase: 'linkedin-message',
          tone: 'professional',
          ...context
        }
      });
      
      if (result.data.success) {
        const content = result.data.result;
        
        // Try to extract subject and message from the generated content
        const lines = content.split('\n').filter(line => line.trim());
        let subject = '';
        let message = content;
        
        // Look for subject line patterns
        const subjectLine = lines.find(line => 
          line.toLowerCase().includes('subject:') || 
          line.toLowerCase().includes('subject line:')
        );
        
        if (subjectLine) {
          subject = subjectLine.replace(/subject:?\s*/i, '').trim();
          message = lines.filter(line => line !== subjectLine).join('\n').trim();
        }
        
        return {
          subject: subject || 'Connection Request',
          message: message || content
        };
      } else {
        throw new Error(result.data.error || 'AI generation failed');
      }
    } catch (error) {
      console.error('Error generating LinkedIn message:', error);
      throw new Error(`Failed to generate LinkedIn message: ${error.message}`);
    }
  },

  /**
   * Import contacts from CSV file
   */
  importContactsFromCSV: async (organizationId, file) => {
    try {
      const text = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(file);
      });
      
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      
      // Expected headers: name, type, organisation, industry, email, mobile, linkedin
      const expectedHeaders = ['name', 'type', 'organisation', 'industry', 'email', 'mobile', 'linkedin'];
      
      const contacts = [];
      const errors = [];
      
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        
        if (values.length !== headers.length) {
          errors.push(`Row ${i + 1}: Column count mismatch`);
          continue;
        }
        
        const contact = {};
        headers.forEach((header, index) => {
          if (expectedHeaders.includes(header)) {
            contact[header] = values[index] || '';
          }
        });
        
        // Validate required fields
        if (!contact.name) {
          errors.push(`Row ${i + 1}: Name is required`);
          continue;
        }
        
        // Set default type if not provided
        if (!contact.type || !['Person', 'Organisation'].includes(contact.type)) {
          contact.type = 'Person';
        }
        
        // Validate email format if provided
        if (contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
          errors.push(`Row ${i + 1}: Invalid email format`);
          continue;
        }
        
        contacts.push(contact);
      }
      
      if (errors.length > 0) {
        console.warn('Import errors:', errors);
      }
      
      // Save all valid contacts
      const savedContacts = [];
      for (const contact of contacts) {
        try {
          const savedContact = await contactsService.createContact(organizationId, contact);
          savedContacts.push(savedContact);
        } catch (err) {
          console.error('Error saving contact:', err);
          errors.push(`Failed to save contact: ${contact.name}`);
        }
      }
      
      return savedContacts;
    } catch (error) {
      console.error('Error importing contacts:', error);
      throw new Error(`Failed to import contacts: ${error.message}`);
    }
  },

  /**
   * Export contacts to CSV file
   */
  exportContactsToCSV: async (contacts) => {
    try {
      const headers = ['Name', 'Type', 'Organisation', 'Industry', 'Email', 'Mobile', 'LinkedIn'];
      const csvContent = [
        headers.join(','),
        ...contacts.map(contact => [
          contact.name || '',
          contact.type || '',
          contact.organisation || '',
          contact.industry || '',
          contact.email || '',
          contact.mobile || '',
          contact.linkedin || ''
        ].map(field => `"${field}"`).join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `contacts-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      return true;
    } catch (error) {
      console.error('Error exporting contacts:', error);
      throw new Error(`Failed to export contacts: ${error.message}`);
    }
  },

  /**
   * Search contacts
   */
  searchContacts: async (organizationId, searchTerm) => {
    try {
      const contacts = await contactsService.getContacts(organizationId);
      
      if (!searchTerm.trim()) {
        return contacts;
      }
      
      const term = searchTerm.toLowerCase();
      return contacts.filter(contact => 
        contact.name?.toLowerCase().includes(term) ||
        contact.email?.toLowerCase().includes(term) ||
        contact.organisation?.toLowerCase().includes(term) ||
        contact.industry?.toLowerCase().includes(term) ||
        contact.mobile?.includes(term)
      );
    } catch (error) {
      console.error('Error searching contacts:', error);
      throw new Error(`Failed to search contacts: ${error.message}`);
    }
  },

  /**
   * Get contact statistics
   */
  getContactStats: async (organizationId) => {
    try {
      const contacts = await contactsService.getContacts(organizationId);
      
      return {
        total: contacts.length,
        people: contacts.filter(c => c.type === 'Person').length,
        organisations: contacts.filter(c => c.type === 'Organisation').length,
        withEmail: contacts.filter(c => c.email).length,
        withMobile: contacts.filter(c => c.mobile).length,
        withLinkedIn: contacts.filter(c => c.linkedin).length,
        byIndustry: contacts.reduce((acc, contact) => {
          if (contact.industry) {
            acc[contact.industry] = (acc[contact.industry] || 0) + 1;
          }
          return acc;
        }, {})
      };
    } catch (error) {
      console.error('Error getting contact stats:', error);
      throw new Error(`Failed to get contact stats: ${error.message}`);
    }
  }
};

export default contactsService;